/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import type { FieldErrors } from 'react-hook-form';
import type { TabId } from './TabNavigation';
import type { PropertyFormValues } from './schema';

type FieldName = keyof PropertyFormValues;

/**
 * Maps each form field to the tab that renders it. The `dados` tab owns the
 * type/purpose and address sections; the `descricao` tab owns the ad content.
 * This is the single source of truth for validation routing.
 */
const FIELD_TO_TAB: Partial<Record<FieldName, TabId>> = {
  category: 'dados',
  transaction_type: 'dados',
  postal_code: 'dados',
  street: 'dados',
  number: 'dados',
  neighborhood: 'dados',
  city: 'dados',
  state: 'dados',
  total_area: 'dados',
  constructed_area: 'dados',
  sale_price: 'dados',
  rental_price: 'dados',
  condominium_price: 'dados',
  iptu: 'dados',
  property_title: 'descricao',
  property_code: 'descricao',
  description: 'descricao',
  owner_client_id: 'descricao',
  admin_percentage: 'descricao',
  lease_term_in_months: 'descricao',
  key_location: 'descricao',
  partner_name: 'descricao',
  exclusive_until: 'descricao',
  publish_authorized_until: 'descricao',
};

/**
 * Resolves the tab that owns a given field, defaulting to the `dados` tab.
 */
export function tabForField(field: string): TabId {
  return FIELD_TO_TAB[field as FieldName] ?? 'dados';
}

/**
 * Returns the distinct tabs that contain at least one errored field.
 */
export function tabsWithErrors(errors: FieldErrors<PropertyFormValues>): TabId[] {
  const tabs = new Set<TabId>();
  for (const field of Object.keys(errors)) {
    tabs.add(tabForField(field));
  }
  return [...tabs];
}

/**
 * Picks the first errored field on the given tab, or undefined when none.
 */
export function firstErroredFieldOnTab(
  errors: FieldErrors<PropertyFormValues>,
  tab: TabId,
): string | undefined {
  return Object.keys(errors).find((field) => tabForField(field) === tab);
}

/**
 * Picks the tab to route to on submit-with-errors, preferring `dados`.
 */
export function firstErroredTab(
  errors: FieldErrors<PropertyFormValues>,
): TabId | undefined {
  const tabs = tabsWithErrors(errors);
  if (tabs.includes('dados')) return 'dados';
  return tabs[0];
}
